import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  ClipboardList,
  FileText,
  FileCheck,
  CalendarPlus,
  Activity,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card } from "./ui/card";
import api from "../lib/api";

const ICONS = {
  "user-plus": UserPlus,
  "clipboard-list": ClipboardList,
  "file-text": FileText,
  "file-check": FileCheck,
  "calendar-plus": CalendarPlus,
  activity: Activity,
};

const formatRelative = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "今";
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}日前`;
  return d.toLocaleDateString("ja-JP", { month: "short", day: "numeric" });
};

// Preview & page sizes
const PREVIEW_COUNT = 3;
const PAGE_SIZE = 20;

export default function RecentActivity() {
  const nav = useNavigate();
  // Preview (collapsed) items - small static load
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(true);
  // Expanded paginated state
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const sentinelRef = useRef(null);

  // Load preview on mount (small payload, no scroll)
  useEffect(() => {
    api
      .get("/dashboard/activity", { params: { limit: PREVIEW_COUNT } })
      .then((r) => setPreview(r.data?.items || []))
      .finally(() => setPreviewLoading(false));
  }, []);

  // Loader for paginated full list
  const loadPage = useCallback(
    async (off) => {
      setPageLoading(true);
      try {
        const r = await api.get("/dashboard/activity", {
          params: { limit: PAGE_SIZE, offset: off },
        });
        const newItems = r.data?.items || [];
        setItems((prev) => (off === 0 ? newItems : [...prev, ...newItems]));
        setOffset(r.data?.next_offset ?? off + newItems.length);
        setHasMore(!!r.data?.has_more);
      } finally {
        setPageLoading(false);
      }
    },
    [],
  );

  // When user expands, fetch first page
  useEffect(() => {
    if (expanded && items.length === 0) {
      loadPage(0);
    }
  }, [expanded, items.length, loadPage]);

  // Infinite scroll: observe sentinel inside the scroll container
  useEffect(() => {
    if (!expanded || !hasMore || pageLoading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadPage(offset);
        }
      },
      { rootMargin: "120px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [expanded, hasMore, pageLoading, offset, loadPage]);

  const totalShown = expanded ? items.length : preview.length;
  const visible = expanded ? items : preview;
  const displayLoading = expanded ? items.length === 0 && pageLoading : previewLoading;

  return (
    <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="recent-activity-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="recent-activity-toggle"
        className="w-full flex items-center justify-between mb-4 group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2C5545]/10 flex items-center justify-center">
            <Clock size={18} className="text-[#2C5545]" strokeWidth={1.5} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-[#1C1C1A]">最近の活動</h2>
              {totalShown > 0 && (
                <span className="text-[10px] bg-white border border-[#E5E5E3] px-2 py-0.5 rounded-full font-numeric font-medium text-[#2C5545]">
                  {expanded ? `${items.length}件読込済` : `直近${preview.length}件`}
                </span>
              )}
            </div>
            <p className="text-xs text-[#8A8A86]">
              {expanded
                ? "スクロールでさらに古い履歴を表示"
                : `直近 ${PREVIEW_COUNT}件を表示中 · クリックで全件表示`}
            </p>
          </div>
        </div>
        <div className="w-9 h-9 rounded-full border border-[#E5E5E3] flex items-center justify-center group-hover:bg-[#F3F3F1] transition shrink-0 ml-2">
          {expanded ? (
            <ChevronUp size={16} className="text-[#4A4A48]" />
          ) : (
            <ChevronDown size={16} className="text-[#4A4A48]" />
          )}
        </div>
      </button>

      {displayLoading ? (
        <div className="py-12 text-center text-[#8A8A86] text-sm">読み込み中...</div>
      ) : visible.length === 0 ? (
        <div className="py-12 text-center text-[#8A8A86] text-sm">最近の活動はまだありません</div>
      ) : (
        <div
          className={
            expanded
              ? "max-h-[420px] overflow-y-auto pr-2 -mr-2 scroll-smooth"
              : ""
          }
          data-testid={expanded ? "activity-scroll-container" : undefined}
        >
          <ol className="relative" data-testid="activity-timeline">
            <span className="absolute left-[19px] top-2 bottom-2 w-px bg-[#E5E5E3]" aria-hidden="true" />
            {visible.map((it, idx) => {
              const Icon = ICONS[it.icon] || Activity;
              return (
                <li
                  key={`${it.kind}-${it.ts}-${idx}`}
                  data-testid={`activity-item-${idx}`}
                  className="relative pl-12 pb-4 last:pb-0 group cursor-pointer"
                  onClick={() => {
                    if (it.patient_id) nav(`/patients/${it.patient_id}`);
                  }}
                >
                  <span
                    className="absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                    style={{ background: it.color + "15" }}
                  >
                    <Icon size={16} style={{ color: it.color }} strokeWidth={1.7} />
                  </span>
                  <div className="rounded-lg p-3 -ml-1 group-hover:bg-[#FBFBF9] transition">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="font-medium text-sm text-[#1C1C1A] leading-snug">{it.title}</div>
                      <div className="text-[10px] text-[#8A8A86] font-numeric tracking-wider shrink-0">
                        {formatRelative(it.ts)}
                      </div>
                    </div>
                    {it.subtitle && (
                      <div className="text-xs text-[#4A4A48] mt-1 line-clamp-2">{it.subtitle}</div>
                    )}
                    {it.actor && (
                      <div className="text-[10px] text-[#8A8A86] mt-1 font-numeric">担当: {it.actor}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Infinite scroll sentinel + status row (only in expanded mode) */}
          {expanded && (
            <div
              ref={sentinelRef}
              data-testid="activity-load-more-sentinel"
              className="py-4 text-center text-[11px] text-[#8A8A86] font-numeric tracking-wider"
            >
              {pageLoading
                ? "読み込み中..."
                : hasMore
                  ? "▾ さらに古い活動を読み込み"
                  : `─ 以上 ${items.length} 件 ─`}
            </div>
          )}
        </div>
      )}

      {!previewLoading && !expanded && preview.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="recent-activity-show-more"
          className="mt-2 w-full py-2 text-xs font-numeric tracking-wider text-[#2C5545] hover:bg-[#F3F3F1] rounded-md transition border border-dashed border-[#E5E5E3] hover:border-[#2C5545]/40"
        >
          全件表示 ▾
        </button>
      )}
    </Card>
  );
}
